/**
 * @file 学员绑定 / 修改教师弹窗
 * @description 按科目维度绑定一位教师（`/pc/student/bindTeacher`）；
 * 教师选项来自 `/pc/teacher/ddl`，按该行科目过滤。
 *
 * @whenToUse 学员列表「所属教师」列行内修改。
 * @whenNotToUse 编辑表单内选择教师；校区教师名单查看。
 */

import { useEffect, useState } from 'react'
import { fetchTeacherDdl, type TeacherItem } from '@/api/teacher.ts'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Select } from '@/components/ui/select.tsx'

export type StudentBindTeacherDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 科目（按学科过滤教师下拉） */
  subjects?: string
  /** 当前绑定教师 id */
  currentTeacherId?: string
  /** 确认；teacherId 为空串表示解绑 */
  onConfirm: (teacherId: string) => void
}

/**
 * 绑定 / 修改教师 Dialog
 */
export function StudentBindTeacherDialog({
  open,
  onOpenChange,
  subjects,
  currentTeacherId,
  onConfirm,
}: StudentBindTeacherDialogProps) {
  const [teachers, setTeachers] = useState<TeacherItem[]>([])
  const [teacherId, setTeacherId] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setTeacherId(currentTeacherId ?? '')
    setError('')
    void fetchTeacherDdl(subjects ? { subjects } : undefined)
      .then(setTeachers)
      .catch(() => setTeachers([]))
  }, [open, subjects, currentTeacherId])

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={currentTeacherId ? '修改老师' : '绑定老师'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            onClick={() => {
              onConfirm(teacherId)
              onOpenChange(false)
            }}
          >
            {currentTeacherId ? '确认修改' : '确认绑定'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          为当前科目选择所属老师；选择「解绑」可清除绑定。
        </p>
        <FormField label="老师" required error={error}>
          <Select
            value={teacherId}
            options={[
              ...teachers.map((teacher) => ({
                value: teacher.id,
                label: teacher.teacherName,
              })),
              { value: '', label: '解绑' },
            ]}
            placeholder="请选择老师"
            invalid={Boolean(error)}
            onChange={(value) => {
              setTeacherId(value)
              setError('')
            }}
          />
        </FormField>
      </div>
    </Dialog>
  )
}
